import { z } from "zod";

/**
 * One play-testing note from the in-game feedback widget: text (typed, or transcribed from a
 * recorded clip on the dev machine) and where in the game it was. A recorded note starts as
 * `queued` on the device (the clip waits in an outbox until the server has it), then
 * `transcribing` with empty text, and fills in when the transcriber is done.
 *
 * Dev tool: the widget is behind the `feedback_widget` flag and off in public builds, and
 * transcription runs on the developer's own machine (docs/feedback.md).
 */
/**
 * The feedback widget's guest id (`<uuid>`, no `guest:` prefix) for a tester who is not signed
 * in. The widget authenticates with the session cookie (plus `X-Charter-CSRF`) and sends this
 * header too; the server uses it only when no session cookie resolves.
 */
export const HEADER_FEEDBACK_GUEST = "x-charter-guest";

/** Caps on a note's attached log: lines, and characters per line. */
export const FEEDBACK_LOG_LINES = 200;
export const FEEDBACK_LOG_LINE_MAX = 400;

export const FeedbackNoteSchema = z.object({
  id: z.string().min(1).max(64),
  at: z.string().min(1).max(64),
  text: z.string().max(8000),
  context: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
  /** The tester's display name at the time, if the project sets one, for the reader. */
  author: z.string().max(64).optional(),
  /** The server's id for the device that sent it (a guest token today). Set server-side. */
  playerId: z.string().max(128).optional(),
  /** Set on recorded notes: the clip's file name and how far its transcription got. */
  audio: z.string().max(200).optional(),
  status: z.enum(["queued", "transcribing", "done", "failed"]).optional(),
  error: z.string().max(400).optional(),
  /**
   * The device's recent log around the note, oldest first, one "HH:MM:SS.mmm text" line each:
   * connection changes, server rejections, warnings and errors, plus a state line or two. Sent
   * after the clip (`POST /feedback/:id/logs`) so whoever fixes the note sees what happened.
   */
  logs: z.array(z.string().max(FEEDBACK_LOG_LINE_MAX)).max(FEEDBACK_LOG_LINES).optional(),
  /** The server's events for the tester's character around the note (added by the dev Mac's sync). */
  serverLogs: z.array(z.string().max(FEEDBACK_LOG_LINE_MAX)).max(FEEDBACK_LOG_LINES).optional(),
});
export type FeedbackNote = z.infer<typeof FeedbackNoteSchema>;
